import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { startRegistration } from '@simplewebauthn/browser';
import { api } from '../../lib/api.js';
import type { Me } from '../../lib/types.js';
import type { PersonalToken, Settings } from '../../lib/settings-types.js';
import {
  Button,
  Card,
  CopyButton,
  EmptyState,
  Field,
  SavedIndicator,
  Select,
  TextInput,
  Toolbar,
  useConfirm,
} from '../../components/ui/index.js';
import { errorMessage, fmtWhen, HOURS, useInstantField, useIsAdmin, useSettingsPost } from './common.js';

/** My account: default crawl hour for new sources, passkeys, the password, and
 * (admins) personal access tokens. */
export function AccountPane({ settings }: { settings: Settings }) {
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/auth/me') });
  const setDefaults = useSettingsPost<{ defaultFetchHour: number | null }>(
    '/api/settings/my-defaults',
  );
  const [passkeyLabel, setPasskeyLabel] = useState('');
  const { confirm, dialog } = useConfirm();

  const removePasskey = useMutation({
    mutationFn: (id: number) => api.post('/api/auth/webauthn/remove', { id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }),
  });

  // A passkey lives on a device, so the two halves can be removed independently: this drops our
  // half. The dialog says so, because deleting it here does NOT delete it from the phone or laptop
  // it was created on, and that surprises people.
  const confirmRemovePasskey = async (k: { id: number; label: string | null }) => {
    const name = k.label ?? `passkey #${k.id}`;
    const ok = await confirm<true>({
      title: `Remove ${name}?`,
      body: (
        <p>
          That device can no longer sign in with this passkey. You can still sign in with your
          password, and enrol it again later. Your device may keep its own copy — remove it there
          too if you want it gone entirely.
        </p>
      ),
      choices: [{ label: 'Remove passkey', value: true, variant: 'danger' }],
    });
    if (ok) removePasskey.mutate(k.id);
  };

  const hour = useInstantField<number | null>(me.data?.defaultFetchHour ?? null, async (v) => {
    const res = await setDefaults.mutateAsync({ defaultFetchHour: v });
    await qc.invalidateQueries({ queryKey: ['me'] });
    return res;
  });

  const addPasskey = useMutation({
    mutationFn: async () => {
      const opts = await api.post<{ challengeId: string; publicKey: never }>(
        '/api/auth/webauthn/register-options',
      );
      const credential = await startRegistration({ optionsJSON: opts.publicKey });
      await api.post('/api/auth/webauthn/register-verify', {
        challengeId: opts.challengeId,
        credential,
        // Named at enrolment, so a list of several is tellable apart. Every
        // passkey used to be labelled 'added from settings'.
        label: passkeyLabel.trim() || `added ${new Date().toLocaleDateString()}`,
      });
    },
    onSuccess: () => {
      setPasskeyLabel('');
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  return (
    <div>
      <Card
        title="My defaults"
        description="New sources you own start on this crawl hour (each source keeps its own hour afterwards — change those on the Providers pane)."
      >
        <div className="cfg-grid">
          <Field label="Default crawl hour">
            {(aria) => (
              <Toolbar>
                <Select
                  {...aria}
                  value={hour.value ?? ''}
                  onChange={(e) => hour.set(e.target.value === '' ? null : Number(e.target.value))}
                >
                  <option value="">{`global (${settings.defaultFetchHour}:00)`}</option>
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}:00
                    </option>
                  ))}
                </Select>
                <SavedIndicator state={hour.state} />
              </Toolbar>
            )}
          </Field>
        </div>
      </Card>

      <Card
        title="Passkeys"
        description="Sign in with Face ID / Touch ID / a security key instead of the password."
      >
        <div className="cfg-grid">
          <Field label="Name this passkey" hint="Optional — helps tell devices apart later.">
            {(aria) => (
              <TextInput
                {...aria}
                value={passkeyLabel}
                placeholder="e.g. work laptop"
                onChange={(e) => setPasskeyLabel(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Toolbar className="cfg-actions">
          <Button onClick={() => addPasskey.mutate()} loading={addPasskey.isPending}>
            Add a passkey
          </Button>
          {addPasskey.isError ? (
            <span className="ui-field__error">{errorMessage(addPasskey.error)}</span>
          ) : null}
        </Toolbar>

        {me.data?.passkeys.length ? (
          <table className="stat-table">
            <thead>
              <tr>
                <th className="l">Passkey</th>
                <th>Added</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {me.data.passkeys.map((k) => (
                <tr key={k.id}>
                  <td className="l">{k.label ?? `passkey #${k.id}`}</td>
                  <td>{fmtWhen(k.createdAt)}</td>
                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      loading={removePasskey.isPending && removePasskey.variables === k.id}
                      onClick={() => void confirmRemovePasskey(k)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState>No passkeys yet — you sign in with the password below.</EmptyState>
        )}
        {removePasskey.isError ? (
          <span className="ui-field__error">{errorMessage(removePasskey.error)}</span>
        ) : null}
      </Card>

      <Card
        title="Password"
        description="Changing it signs out every other device. Do this first thing after an account was migrated from the old system."
      >
        <PasswordForm />
      </Card>
      {isAdmin ? <PersonalTokens confirm={confirm} /> : null}
      {dialog}
    </div>
  );
}

/**
 * A personal token is you, on the API: anything you can do signed in, a
 * request carrying it can do too. That is the point (an automated helper can
 * work on your behalf) and the reason the card reads like the password one:
 * shown once, expires, revocable, and revocation is final.
 */
function PersonalTokens({ confirm }: { confirm: ReturnType<typeof useConfirm>['confirm'] }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(30);
  const [minted, setMinted] = useState<{ label: string; token: string; expires: string } | null>(null);
  const tokens = useQuery({
    queryKey: ['personal-tokens'],
    queryFn: () => api.get<{ tokens: PersonalToken[] }>('/api/personal-tokens'),
  });
  const mint = useMutation({
    mutationFn: () =>
      api.post<{ token: string; label: string; expires: string }>('/api/personal-tokens', { label, days }),
    onSuccess: (res) => {
      setMinted(res);
      setLabel('');
      void qc.invalidateQueries({ queryKey: ['personal-tokens'] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: number) => api.post('/api/personal-tokens/revoke', { id }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['personal-tokens'] }),
  });

  return (
    <Card
      title="Personal access tokens"
      description="A token that acts as you on the API — everything you can do signed in, a request carrying it can do. Send it as “Authorization: Bearer …”. Shown once; expires; revoke it here the moment it is no longer needed."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mint.mutate();
        }}
      >
        <div className="cfg-grid">
          <Field label="What will use this token?" required>
            {(aria) => (
              <TextInput {...aria} value={label} placeholder="e.g. Claude, testing" onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          <Field label="Expires after">
            {(aria) => (
              <Select {...aria} value={days} onChange={(e) => setDays(Number(e.target.value))}>
                <option value={1}>1 day</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={365}>1 year</option>
              </Select>
            )}
          </Field>
        </div>
        <Toolbar className="cfg-actions">
          <Button type="submit" loading={mint.isPending} disabled={!label.trim()}>
            Mint token
          </Button>
          {mint.isError ? <span className="ui-field__error">{errorMessage(mint.error)}</span> : null}
        </Toolbar>
      </form>

      {minted ? (
        <div className="warnbox ok">
          <p>
            Token for <b>{minted.label}</b>, valid until {fmtWhen(minted.expires)} — copy it now, it will not be
            shown again:
          </p>
          <div className="mono token-once">{minted.token}</div>
          <Toolbar className="cfg-actions">
            <CopyButton value={minted.token} label="Copy token" />
            <Button variant="ghost" onClick={() => setMinted(null)}>
              Done
            </Button>
          </Toolbar>
        </div>
      ) : null}

      {tokens.data?.tokens.length ? (
        <table className="stat-table">
          <thead>
            <tr>
              <th className="l">Label</th>
              <th className="l">Token</th>
              <th>Expires</th>
              <th>Last used</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {tokens.data.tokens.map((t) => (
              <tr key={t.id} className={t.expired || !t.enabled ? 'row-disabled' : undefined}>
                <td className="l">{t.label}</td>
                <td className="l mono">{t.preview}</td>
                <td>{t.expired ? `expired ${fmtWhen(t.expires)}` : fmtWhen(t.expires)}</td>
                <td>{t.lastUsed ? fmtWhen(t.lastUsed) : 'never'}</td>
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    loading={revoke.isPending && revoke.variables === t.id}
                    onClick={() => {
                      void (async () => {
                        const ok = await confirm<true>({
                          title: `Revoke the token for ${t.label}?`,
                          body: <p>Requests using it fail immediately. This cannot be undone — mint a new one instead.</p>,
                          choices: [{ label: 'Revoke it', value: true, variant: 'danger' }],
                        });
                        if (ok) revoke.mutate(t.id);
                      })();
                    }}
                  >
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState>No personal tokens.</EmptyState>
      )}
      {revoke.isError ? <span className="ui-field__error">{errorMessage(revoke.error)}</span> : null}
    </Card>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const change = useMutation({
    mutationFn: () => api.post('/api/auth/password', { current, next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setAgain('');
    },
  });

  const tooShort = next.length > 0 && next.length < 8;
  // A typo here signs out every other device and leaves you with a password you
  // don't know, so it is worth typing twice.
  const mismatch = again.length > 0 && next !== again;
  const ready = current.length > 0 && next.length >= 8 && next === again;

  return (
    // A real <form>: Enter submits, and a password manager can associate the
    // fields with it. This was a <div> with two autoComplete inputs floating in it.
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) change.mutate();
      }}
    >
      <div className="cfg-grid">
        <Field label="Current password">
          {(aria) => (
            <TextInput
              {...aria}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          )}
        </Field>
        <div />
        <Field
          label="New password"
          hint="At least 8 characters."
          error={tooShort ? 'Too short — 8 characters or more.' : undefined}
        >
          {(aria) => (
            <TextInput
              {...aria}
              type="password"
              reveal
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </Field>
        <Field label="New password again" error={mismatch ? "These don't match." : undefined}>
          {(aria) => (
            <TextInput
              {...aria}
              type="password"
              autoComplete="new-password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
            />
          )}
        </Field>
      </div>
      <Toolbar className="cfg-actions">
        <Button type="submit" loading={change.isPending} disabled={!ready}>
          Change password
        </Button>
        {change.isError ? (
          <span className="ui-field__error">{errorMessage(change.error)}</span>
        ) : null}
        {change.isSuccess ? (
          <span className="ui-saved ui-saved--ok" role="status">
            ✓ Changed — other devices signed out
          </span>
        ) : null}
      </Toolbar>
    </form>
  );
}
